import { useMutation } from "@apollo/client";
import {
  colors,
  icon,
  RotaButton,
  RotaButtonNew,
  RotaButtonIcon,
  RotaModal,
  RotaSnackBar
} from "@teamrota/rota-design";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { POOL_TYPES } from "@teamrota/rota-common";
import { useCurrency } from "~/src/containers/profile-loader";
import {
  FeedbackWrapper,
  Label
} from "~/src/containers/timesheets/components/timesheet/components/rating/rating.styles";
import { UPDATE_BOOKING_STATUS } from "~/src/containers/timesheets/graphql/update-booking-status";
import { errorModal } from "~/src/utils/errors";
import { getFormatterMoney } from "~/src/utils/formatting";
import { hoursAndMinutesDisplay } from "~/src/utils/formatting-hours-timesheet";
import Attendance from "../attendance";
import CheckInOut from "../check-in-out";
import DialogBadRate from "../dialog/bad-rate";
import DialogTurnedAway from "../dialog/turned-away";
import Member from "../member";
import Rating from "../rating";
import ScheduledHours from "../scheduled-hours";
import {
  BreakInput,
  StyledBookingColumn,
  StyledBookingColumnPayRates,
  StyledBookingGridColumn,
  StyledColumnPayRates,
  StyledGridColumn,
  StyledGridColumnPayRates,
  StyledTextArea,
  StyledWrapButton,
  StyledWrapEditMode,
  StyledWrapFeedbackRating,
  StyledWrapGrid,
  ButtonsWrapper
} from "./timesheet-row.styles";
import { useCreateOrUpdateRelationship } from "./use-create-or-update-relationship";

const feedbackOptions = [
  "Punctuality",
  "Wrong Uniform",
  "Unkempt presentation",
  "Skills",
  "Attitude",
  "Damages the brand"
];

const NO_SHOW_ACTION = "no-show";
const TURNED_AWAY_ACTION = "turned-away";

const TimesheetRow = ({
  bookingData,
  setBookingUpdate,
  handleApprove,
  isApproved,
  isUncalculatedRate,
  setIsApproveEnabled
}) => {
  const { TurnedAwayIcon, StarIcons } = icon;
  const { booking, member, bookingState } = bookingData;
  const [
    createOrUpdateRelationship,
    { loading }
  ] = useCreateOrUpdateRelationship();

  const isBlacklisted = member.poolType === POOL_TYPES.BLACKLISTED;

  const { currencyCode, currencySymbol } = useCurrency();

  const formatterMoney = getFormatterMoney(currencyCode);

  const stateRowInitialObject = {
    startOnApproval: booking?.startFinal,
    endOnApproval: booking?.endFinal,
    breakOnApproval: booking?.breakFinal || 0,
    ratingOfMember: booking?.ratingOfMember,
    feedbackForMember: booking?.feedbackForMember,
    feedbackLabelsForMember: booking?.feedbackLabelsForMember,
    isFavourited: member?.poolType === POOL_TYPES.FAVOURITED,
    notes: booking?.notes,
    isNoShow: booking?.isNoShow,
    isTurnedAway: booking.isTurnedAway
  };

  const [stateRow, setStateRow] = useState(stateRowInitialObject);
  const [breakInputValue, setBreakInputValue] = useState(
    stateRow?.breakOnApproval
  );
  const [isEditMode, setIsEditMode] = useState(false);
  const [isTurnedAwayModal, setIsTurnedAwayModal] = useState(false);
  const [isBlacklistModalVisible, setIsBlacklistModalVisible] = useState(false);
  const [action, setAction] = useState("");
  const [isOpenModalRatingBadShift, setIsOpenModalBadShift] = useState(false);
  const [badRatingOfMember, setBadRatingOfMember] = useState(0);
  const [snackbar, setSnackBar] = useState({});
  const isFeedbackShown =
    !!stateRow?.ratingOfMember &&
    !isApproved &&
    !stateRow?.isNoShow &&
    !stateRow?.isTurnedAway;

  const isStarRatingEditable =
    !isApproved && !stateRow?.isNoShow && !stateRow?.isTurnedAway;

  useEffect(() => {
    if (isEditMode) setIsApproveEnabled(prev => prev + 1);
    else setIsApproveEnabled(prev => (prev <= 0 ? 0 : prev - 1));
  }, [isEditMode]);

  useEffect(() => {
    setBookingUpdate(prev => {
      const oldBooking = prev.filter(el => el.id !== booking.id);
      return [
        ...oldBooking,
        {
          id: booking?.id,
          ...stateRow
        }
      ];
    });
  }, [stateRow]);

  const handleSave = async () => {
    setIsEditMode(
      await handleApprove({
        isApprovingShift: false,
        bookingForUpdate: { id: booking?.id, ...stateRow }
      })
    );
  };

  const [updateBookingStatus] = useMutation(UPDATE_BOOKING_STATUS, {
    refetchQueries: ["getShiftById"],
    onCompleted: data => {
      setStateRow(prev => {
        return {
          ...prev,
          isNoShow: data.updateBookingState.state === "NO_SHOW",
          isTurnedAway: data.updateBookingState.state === "TURNED_AWAY"
        };
      });
    }
  });

  const onUpdateStartTime = date => {
    setStateRow(prev => {
      return {
        ...prev,
        startOnApproval: moment(date).format()
      };
    });
  };

  const onUpdateEndTime = date => {
    setStateRow(prev => {
      return {
        ...prev,
        endOnApproval: moment(date).format()
      };
    });
  };

  const onFeedbackUpdate = value => {
    setStateRow(prev => {
      return { ...prev, feedbackForMember: value };
    });
  };

  const onUpdateFeedbackLabels = labels => {
    setStateRow(prev => {
      return { ...prev, feedbackLabelsForMember: labels };
    });
  };

  const handleChangeRating = async value => {
    //  work here
    if (stateRow.isNoShow || stateRow.isTurnedAway) {
      return await errorModal(
        `You cannot rate if marked as ${
          stateRow.isTurnedAway ? "Turned Away" : "No Show"
        }`
      );
    }

    // if valued 3 or below and there is a pop up open
    if (value <= 3) {
      setBadRatingOfMember(value);
      setIsOpenModalBadShift(true);
    } else if (value > 3 && value <= 5) {
      // if (memberIsNotFavourited) {
      onToggleFavourite(false);
      setStateRow(prev => {
        return { ...prev, ratingOfMember: value, feedbackLabelsForMember: [] };
      });
      // }
    } else if (value === 6) {
      setStateRow(prev => {
        return { ...prev, ratingOfMember: value, feedbackLabelsForMember: [] };
      });
    }
  };

  const onToggleFavourite = value => {
    if (stateRow.isTurnedAway)
      setStateRow(prev => {
        return {
          ...prev,
          isTurnedAway: false,
          notes: ""
        };
      });
    else if (stateRow.isNoShow)
      setStateRow(prev => {
        return {
          ...prev,
          isNoShow: false
        };
      });

    if (value)
      setStateRow(prev => {
        return {
          ...prev,
          ratingOfMember: 6,
          feedbackLabelsForMember: []
        };
      });

    setStateRow(prev => {
      return {
        ...prev,
        isFavourited: value
      };
    });
  };

  const onConfirmBadRating = async () => {
    if (badRatingOfMember === 3) {
      setStateRow(prev => {
        return { ...prev, feedbackLabelsForMember: [] };
      });
    }
    onToggleFavourite(false);
    setStateRow(prev => {
      return {
        ...prev,
        ratingOfMember: badRatingOfMember
      };
    });
    setBadRatingOfMember(0);
    setIsOpenModalBadShift(false);
  };

  const onChangeBreakInput = event => {
    const { endFinal, startFinal } = booking;
    const { value } = event?.target;

    const fullShiftInminutes = moment
      .duration(
        moment(stateRow?.endOnApproval || endFinal).diff(
          moment(stateRow?.startOnApproval || startFinal)
        )
      )
      .asMinutes();

    if (/^\d*$/.test(value) && value < fullShiftInminutes) {
      setBreakInputValue(Number(value));
      setStateRow(prev => {
        return { ...prev, breakOnApproval: Number(value) };
      });
    }
  };

  const onToggleNoShow = () => {
    try {
      updateBookingStatus({
        variables: {
          id: booking.id,
          isTurnedAway: false,
          isNoShow: !stateRow.isNoShow,
          notes: "",
          feedbackLabelsForMember: [],
          feedbackForMember: "",
          ratingOfMember: 0
        }
      });
      onToggleFavourite(false);
      setSnackBar({
        open: true,
        message: `Saved successfully.`,
        severity: "success"
      });
      setStateRow(stateRowInitialObject);
    } catch {
      setSnackBar({
        open: true,
        message: "Something went wrong. Please try again",
        severity: "error"
      });
    }
    setAction("");
  };

  const onToggleTurnedAway = () => {
    try {
      updateBookingStatus({
        variables: {
          id: booking.id,
          isTurnedAway: !stateRow.isTurnedAway,
          isNoShow: false,
          state: bookingState.state
        }
      });
      onToggleFavourite(false);
      setIsTurnedAwayModal(false);
      setSnackBar({
        open: true,
        message: `Saved successfully.`,
        severity: "success"
      });
    } catch {
      setSnackBar({
        open: true,
        message: "Something went wrong. Please try again",
        severity: "error"
      });
    }
    setAction("");
  };

  const onSaveNotes = value => {
    setStateRow(prev => {
      return { ...prev, notes: value };
    });
  };

  const onChangeNoShow = async () => {
    if (!stateRow.isNoShow) {
      if (!isBlacklisted) {
        setIsBlacklistModalVisible(true);
        setAction(NO_SHOW_ACTION);
      } else {
        onToggleNoShow();
      }
    } else {
      if (isBlacklisted) {
        await createOrUpdateRelationship({
          variables: { poolType: POOL_TYPES.RECOMMENDED, memberId: member.id }
        });
      }
      onToggleNoShow();
    }
  };

  const onChangeTurnedAway = async () => {
    if (!stateRow.isTurnedAway) {
      if (!isBlacklisted) {
        setIsBlacklistModalVisible(true);
        setAction(TURNED_AWAY_ACTION);
      } else {
        setIsTurnedAwayModal(true);
      }
    } else {
      if (isBlacklisted) {
        await createOrUpdateRelationship({
          variables: { poolType: POOL_TYPES.RECOMMENDED, memberId: member.id }
        });
      }
      onToggleTurnedAway();
    }
  };

  const blacklistCleanup = () => {
    setIsBlacklistModalVisible(false);

    if (action === TURNED_AWAY_ACTION) {
      setIsTurnedAwayModal(true);
    }

    if (action === NO_SHOW_ACTION) {
      onToggleNoShow();
    }
  };

  const onBlacklist = async () => {
    await createOrUpdateRelationship({
      variables: { poolType: POOL_TYPES.BLACKLISTED, memberId: member.id },
      onCompleted: blacklistCleanup
    });
  };

  return (
    <div>
      <StyledWrapGrid>
        <StyledGridColumn start={1} vertical>
          Staff member
        </StyledGridColumn>
        <StyledGridColumn start={2}>Scheduled hours</StyledGridColumn>
        <StyledGridColumn start={3}>Check in/out</StyledGridColumn>
        <StyledGridColumn start={4}>Break</StyledGridColumn>
        <StyledGridColumn start={5}>Other</StyledGridColumn>
        <StyledGridColumn start={6}>Rate Shift</StyledGridColumn>
        <StyledWrapButton>
          {isApproved ||
            (!isEditMode && (
              <RotaButtonIcon icon="edit" onClick={() => setIsEditMode(true)} />
            ))}
        </StyledWrapButton>
      </StyledWrapGrid>
      <StyledBookingGridColumn>
        <StyledBookingColumn start={1} padding="10px">
          <Member member={member} />
        </StyledBookingColumn>

        <StyledBookingColumn start={2} justify="center">
          <ScheduledHours
            onUpdateStartTime={date => onUpdateStartTime(date)}
            onUpdateEndTime={date => onUpdateEndTime(date)}
            onToggleEditMode={() => setIsEditMode(!isEditMode)}
            isEditMode={isEditMode}
            startTimeUpdate={stateRow?.startOnApproval}
            endTimeUpdate={stateRow?.endOnApproval}
            isNoShow={stateRow?.isNoShow}
            booking={booking}
            isTurnedAway={stateRow?.isTurnedAway}
            bookingState={bookingState}
          />
        </StyledBookingColumn>

        <StyledBookingColumn start={3} justify="center">
          <CheckInOut booking={booking} bookingState={bookingState} />
        </StyledBookingColumn>

        <StyledBookingColumn start={4} justify="center">
          {!stateRow?.isNoShow &&
            !stateRow?.isTurnedAway &&
            (isEditMode ? (
              <span>
                <BreakInput
                  value={breakInputValue}
                  onChange={onChangeBreakInput}
                />
                &nbsp;min
              </span>
            ) : (
              <span>
                <p>{breakInputValue} min</p>
              </span>
            ))}
        </StyledBookingColumn>
        <StyledBookingColumn start={5} justify="center">
          <Attendance
            isFavourited={stateRow?.isFavourited}
            isNoShow={stateRow?.isNoShow}
            isTurnedAway={stateRow?.isTurnedAway}
            onToggleFavourite={() => onToggleFavourite(!stateRow?.isFavourited)}
            onToggleNoShow={onChangeNoShow}
            onToggleTurnedAway={onChangeTurnedAway}
            isEditMode={isEditMode}
          />
        </StyledBookingColumn>

        <StyledBookingColumn start={6} justify="center">
          <Rating
            onChange={value => handleChangeRating(value)}
            ratingUpdate={badRatingOfMember || stateRow?.ratingOfMember}
            isGrey={stateRow?.isNoShow || stateRow?.isTurnedAway}
            isEditMode={isStarRatingEditable}
          />
        </StyledBookingColumn>
      </StyledBookingGridColumn>
      {isFeedbackShown && (
        <StyledWrapFeedbackRating>
          <FeedbackWrapper
            style={{
              gridColumnStart: 2
            }}
          >
            {stateRow?.feedbackLabelsForMember?.length > 0 && !isEditMode && (
              <>
                <p style={{ marginRight: "8px" }}>Your feedback:&nbsp;</p>
                {stateRow.ratingOfMember < 3 &&
                  stateRow.feedbackLabelsForMember.map(label => (
                    <Label isSelected={true} key={label}>
                      {label}
                    </Label>
                  ))}
              </>
            )}
            {isEditMode && stateRow?.ratingOfMember < 3 && (
              <>
                <p style={{ marginRight: "8px" }}>Provide feedback:&nbsp;</p>
                {stateRow.ratingOfMember < 3 &&
                  feedbackOptions.map(label => (
                    <Label
                      isSelected={
                        stateRow.feedbackLabelsForMember &&
                        stateRow.feedbackLabelsForMember.includes(label)
                      }
                      onClick={() =>
                        onUpdateFeedbackLabels(
                          stateRow.feedbackLabelsForMember &&
                            stateRow.feedbackLabelsForMember.includes(label)
                            ? stateRow.feedbackLabelsForMember.filter(
                                l => l !== label
                              )
                            : [
                                ...(stateRow.feedbackLabelsForMember || []),
                                label
                              ]
                        )
                      }
                      key={label}
                    >
                      {label}
                    </Label>
                  ))}
              </>
            )}
          </FeedbackWrapper>

          <StyledTextArea
            placeholder="Enter Feedback"
            defaultValue={stateRow.feedbackForMember}
            onBlur={e => onFeedbackUpdate(e.target.value)}
            row="2"
            col="5"
            disabled={!isEditMode}
          />
        </StyledWrapFeedbackRating>
      )}
      {isEditMode && (
        <StyledWrapEditMode>
          <div
            style={{
              display: "flex",
              gridColumnStart: 3,
              justifyContent: "flex-end",
              marginBottom: "18px",
              marginRight: "15px"
            }}
          >
            <RotaButton
              variant="secondary"
              size="small"
              onClick={() => {
                setIsEditMode(false);
                setBreakInputValue(stateRowInitialObject.breakOnApproval);
                setStateRow(stateRowInitialObject);
              }}
            >
              Cancel
            </RotaButton>
            <RotaButton
              size="small"
              style={{ margin: "0 17px 0 22px" }}
              onClick={handleSave}
            >
              Save
            </RotaButton>
          </div>
        </StyledWrapEditMode>
      )}
      <StyledGridColumnPayRates>
        <StyledColumnPayRates start={1} bold>
          Payment info
        </StyledColumnPayRates>
        <StyledColumnPayRates start={2}>Regular rate</StyledColumnPayRates>
        <StyledColumnPayRates start={3}>Paid hours</StyledColumnPayRates>
        {!!booking?.overtimeChargeRate && (
          <>
            <StyledColumnPayRates start={4}>Overtime rate</StyledColumnPayRates>
            <StyledColumnPayRates start={5}>Paid hours</StyledColumnPayRates>
          </>
        )}
        {!!booking?.overstayChargeRate && (
          <>
            <StyledColumnPayRates start={4}>Overstay rate</StyledColumnPayRates>
            <StyledColumnPayRates start={5}>Paid hours</StyledColumnPayRates>
          </>
        )}
        <StyledColumnPayRates
          start={
            booking?.overtimeChargeRate || booking?.overstayChargeRate ? 6 : 4
          }
        >
          Bonus
        </StyledColumnPayRates>
      </StyledGridColumnPayRates>
      <StyledBookingColumnPayRates>
        <StyledBookingColumn start={2} justify="center">
          {isUncalculatedRate && "Uncalculated"}
          {!isUncalculatedRate &&
            formatterMoney.format(booking?.chargeRate / 100)}
        </StyledBookingColumn>
        <StyledBookingColumn start={3} justify="center">
          {hoursAndMinutesDisplay(bookingState)}
        </StyledBookingColumn>
        {!!booking?.overtimeChargeRate && (
          <>
            <StyledBookingColumn start={4} justify="center">
              {`${currencySymbol} ${(booking?.overtimeChargeRate / 100).toFixed(
                2
              )}`}
            </StyledBookingColumn>
            <StyledBookingColumn start={5} justify="center">
              {hoursAndMinutesDisplay(
                bookingState,
                booking?.overtimeHoursWorked
              )}
            </StyledBookingColumn>
          </>
        )}
        {!!booking?.overstayChargeRate && (
          <>
            <StyledBookingColumn start={4} justify="center">
              {`${currencySymbol} ${(booking?.overstayChargeRate / 100).toFixed(
                2
              )}`}
            </StyledBookingColumn>
            <StyledBookingColumn start={5} justify="center">
              {hoursAndMinutesDisplay(
                bookingState,
                booking?.overtimeHoursLimit
              )}
            </StyledBookingColumn>
          </>
        )}
        <StyledBookingColumn
          start={
            booking?.overtimeChargeRate || booking?.overstayChargeRate ? 6 : 4
          }
          justify="center"
        >
          {formatterMoney.format(bookingState?.bonusFinal / 100)}
        </StyledBookingColumn>
      </StyledBookingColumnPayRates>

      {isTurnedAwayModal && (
        <RotaModal isSmall onClose={() => setIsTurnedAwayModal(false)}>
          <DialogTurnedAway
            onConfirm={onToggleTurnedAway}
            onCancel={() => setIsTurnedAwayModal(false)}
            firstName={member.firstName}
            lastName={member.lastName}
            onSaveNotes={onSaveNotes}
            notesUpdate={stateRow?.notes}
            icon={
              <TurnedAwayIcon
                color={colors.rotaOrange}
                colorLine={colors.black}
                styles={{ width: "50px", height: "50px" }}
              />
            }
            isInput
          />
        </RotaModal>
      )}
      {isBlacklistModalVisible && (
        <RotaModal isSmall onClose={() => setIsBlacklistModalVisible(false)}>
          <div>Do you want to also blacklist this member?</div>
          <ButtonsWrapper>
            <RotaButtonNew
              onClick={blacklistCleanup}
              disabled={loading}
              variant="outlined"
            >
              No
            </RotaButtonNew>
            <RotaButtonNew onClick={onBlacklist} loading={loading}>
              Yes
            </RotaButtonNew>
          </ButtonsWrapper>
        </RotaModal>
      )}
      {isOpenModalRatingBadShift && (
        <RotaModal
          isSmall
          onClose={() => {
            setIsOpenModalBadShift(false);
            setBadRatingOfMember(0);
          }}
        >
          <DialogBadRate
            onConfirm={() => onConfirmBadRating()}
            onCancel={() => {
              setIsOpenModalBadShift(false);
              setBadRatingOfMember(0);
            }}
            value={badRatingOfMember}
            upcomingAccepted={member.upcomingAccepted}
            icon={
              <StarIcons
                color={colors.red}
                styles={{ width: "50px", height: "50px" }}
              />
            }
          />
        </RotaModal>
      )}
      <RotaSnackBar
        snackOpen={snackbar.open}
        onClose={() => setSnackBar({})}
        message={snackbar.message}
        severity={snackbar.severity}
      />
    </div>
  );
};

export default TimesheetRow;
